import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateUI } from 'store/UI/actions';
import { useUI } from 'store/UI/selectors';
import { isErrorContent } from 'utils/methods/storyContentValidation';

const useSoyHistoriasNotification = ({
  page,
  chapter,
  hasStoryWithAi,
  setHasStoryWithAi,
  openStoryWithAi,
  setOpenStoryWithAi,
  setOpenStoryWithAiView,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const UI = useUI();
  const prevPageRef = useRef(page);
  const prevChapterRef = useRef(chapter);
  const notificationTimeoutRef = useRef(null);

  useEffect(() => {
    const soyView = UI?.soyHistoriasView;
    const isOpen = soyView?.open;
    const storyData = soyView?.data;
    const isNotification = soyView?.isNotification;
    const notificationChapter = soyView?.chapter;
    const notificationPage = soyView?.page;

    const pageChanged =
      prevPageRef.current !== page || prevChapterRef.current !== chapter;

    if (!isOpen || !storyData) {
      prevPageRef.current = page;
      prevChapterRef.current = chapter;
      return;
    }

    if (isNotification && !openStoryWithAi) {
      const urlParams = new URLSearchParams(location.search);
      const urlChapter = urlParams.get("chapter");
      const urlPage = urlParams.get("page");
      
      const matchesCurrentPage = notificationChapter && notificationPage && 
                                  ((Number(notificationChapter) === Number(chapter) && 
                                    Number(notificationPage) === Number(page)) ||
                                   (urlChapter && urlPage && 
                                    Number(notificationChapter) === Number(urlChapter) && 
                                    Number(notificationPage) === Number(urlPage)));
      
      if (matchesCurrentPage) {
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
        setHasStoryWithAi([storyData]);
        setOpenStoryWithAi(true);
        setOpenStoryWithAiView(true);
        prevPageRef.current = page;
        prevChapterRef.current = chapter;
        return;
      } else if (!notificationTimeoutRef.current) {
        notificationTimeoutRef.current = setTimeout(() => {
          const currentUrlParams = new URLSearchParams(window.location.search);
          const currentUrlChapter = currentUrlParams.get("chapter");
          const currentUrlPage = currentUrlParams.get("page");
          
          const stillMatches = notificationChapter && notificationPage && 
                              currentUrlChapter && currentUrlPage && 
                              Number(notificationChapter) === Number(currentUrlChapter) && 
                              Number(notificationPage) === Number(currentUrlPage);
          
          if (!stillMatches) {
            dispatch(updateUI({
              soyHistoriasView: {
                open: false,
                data: null,
                isNotification: false
              }
            }));
          }
          notificationTimeoutRef.current = null;
        }, 200);
      }
      prevPageRef.current = page;
      prevChapterRef.current = chapter;
      return;
    }

    if (isNotification && pageChanged && openStoryWithAi) {
      const matchesNotificationPage = notificationChapter && notificationPage && 
                                      Number(notificationChapter) === Number(chapter) && 
                                      Number(notificationPage) === Number(page);
      
      const hasStoryOnNewPage = Array.isArray(hasStoryWithAi) && 
        hasStoryWithAi.filter(item => !isErrorContent(item?.story_content || '')).length > 0;
      
      if (matchesNotificationPage) {
        setHasStoryWithAi([storyData]);
        setOpenStoryWithAi(true);
        setOpenStoryWithAiView(true);
      } else if (hasStoryOnNewPage) {
        setOpenStoryWithAi(true);
        setOpenStoryWithAiView(true);
        dispatch(updateUI({
          soyHistoriasView: {
            open: false,
            data: null,
            isNotification: false
          }
        }));
      } else {
        setOpenStoryWithAi(false);
        setOpenStoryWithAiView(false);
        dispatch(updateUI({
          soyHistoriasView: {
            open: false,
            data: null,
            isNotification: false
          }
        }));
      }
    }

    prevPageRef.current = page;
    prevChapterRef.current = chapter;
    
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [
    UI?.soyHistoriasView?.open,
    UI?.soyHistoriasView?.data,
    UI?.soyHistoriasView?.isNotification,
    UI?.soyHistoriasView?.chapter,
    UI?.soyHistoriasView?.page,
    page,
    chapter,
    dispatch,
    openStoryWithAi,
    hasStoryWithAi,
    location.search,
    setHasStoryWithAi,
    setOpenStoryWithAi,
    setOpenStoryWithAiView,
  ]);
};

export default useSoyHistoriasNotification;

